import useSWR, { mutate } from "swr";
import { useCallback, useState } from "react";
import { adminPaymentsService } from "../data/admin-payments.service";
import type {
  PayVendorBillDto,
  UnpaidBillsQueryDto,
  VendorBillDto,
} from "../data/types";
import type { PaginatedResponse } from "../../pagination/data/types/pagination";

const KEY_PREFIX = "admin-supplier-unpaid-bills";
const buildKey = (supplierId: number | null, query: UnpaidBillsQueryDto) =>
  supplierId == null ? null : [KEY_PREFIX, supplierId, JSON.stringify(query)] as const;

export function useSupplierUnpaidBills(
  supplierId: number | null | undefined,
  query: UnpaidBillsQueryDto = {},
) {
  const id = typeof supplierId === "number" ? supplierId : null;
  const swr = useSWR<PaginatedResponse<VendorBillDto>>(
    buildKey(id, query),
    async () => {
      const res = await adminPaymentsService.listSupplierUnpaidBills(id!, query);
      return res.data;
    },
  );
  return swr;
}

/**
 * Tells SWR to re-fetch every cached unpaid-bills list (any supplier, any
 * query). Use after a successful payment so the row disappears.
 */
export async function revalidateAllUnpaidBills() {
  await mutate(
    (key) => Array.isArray(key) && key[0] === KEY_PREFIX,
    undefined,
    { revalidate: true },
  );
}

export function usePayVendorBill() {
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const pay = useCallback(async (dto: PayVendorBillDto) => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminPaymentsService.payVendorBill(dto);
      // Refresh the unpaid-bills list (row should disappear) and the admin
      // payments list (new payment appears).
      await Promise.all([
        revalidateAllUnpaidBills(),
        mutate(
          (key) => typeof key === "string" && key.startsWith("admin-payments"),
          undefined,
          { revalidate: true },
        ),
      ]);
      return res.data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { pay, isLoading, error };
}
